import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'

import RegionsRadioBlock from './RegionsRadioBlock'
import Locations from './Locations'
import Spoiler from '../../../components/Spoiler'
import RadioBlock from '../../../components/Form/RadioBlock'
import MultiSelectBox from '../../../features/components/Form/MultiSelectBox'
import GeolocationsExclusions from './GeolocationsExclusions'

import { formatCountries } from './helpers'

import { getChoicesCountries } from '../../../modules/actions/choices'
import {
  choicesCountriesSelector,
  choicesCountriesWasLoadedSelector,
  currentUserCountrySelector
} from '../../../modules/selectors/choices'

import {
  SHOW_OPTION,
  SHOW_COUNTRIES,
  SHOW_COUNTRY_CHOICE,
  SHOW_OPTION_WORLDWIDE,
  SHOW_OPTION_COUNTRIES
} from './fields'

const Geolocations = ({
  formik,
  platform,
  showRegionsCountryFilter = true,
  showRegions = true,
  showCustomLocations = true,
  showWorldwide = true,
  showGeolocationsExclusions = false,
  countriesFilter
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const { values, errors, touched, setFieldValue } = formik

  const countriesError = errors[SHOW_COUNTRIES]
  const countiesTouched = touched[SHOW_COUNTRIES]
  const selectedCountries = values[SHOW_COUNTRIES]

  const countries = useSelector(choicesCountriesSelector)
  const countriesWasLoaded = useSelector(choicesCountriesWasLoadedSelector)
  const currentUserCountry = useSelector(currentUserCountrySelector)

  const formattedCountries = useMemo(() => formatCountries(countries, countriesFilter), [countries, countriesFilter])

  const currentUserCountryIsAvailable = useMemo(
    () => formattedCountries.find(item => item.value === currentUserCountry?.id),
    [formattedCountries, currentUserCountry]
  )

  const setUserCountry = useCallback(() => {
    setFieldValue(SHOW_COUNTRIES, [
      {
        value: currentUserCountry.id,
        label: currentUserCountry.name,
        code: currentUserCountry.iso2,
        resourceName: currentUserCountry['resource_name'],
        tiktok_id: currentUserCountry['tiktok_id']
      }
    ])
    setFieldValue(SHOW_COUNTRY_CHOICE, currentUserCountry.id)
  }, [currentUserCountry, setFieldValue])

  const [wasDefaultCountryPreselected, setWasDefaultCountryPreselected] = useState(
    // when set initial state look into values, because country could be preselected between mounting
    // e.g. when user switches routes of the form and step is mounted again
    values[SHOW_COUNTRIES].find(item => item.value === currentUserCountry?.id)
  )

  useEffect(() => {
    // Preselect the user's country if no countries were selected previously
    if (
      currentUserCountry &&
      currentUserCountryIsAvailable &&
      selectedCountries &&
      !selectedCountries.length &&
      !wasDefaultCountryPreselected &&
      countriesWasLoaded
    ) {
      setWasDefaultCountryPreselected(true)
      setUserCountry()
    }
  }, [
    setUserCountry,
    currentUserCountry,
    currentUserCountryIsAvailable,
    selectedCountries,
    setFieldValue,
    wasDefaultCountryPreselected,
    countriesWasLoaded
  ])

  useEffect(() => {
    // getChoicesCountries for SHOW_OPTION_COUNTRIES
    if (!countriesWasLoaded) {
      dispatch(getChoicesCountries())
    }
  }, [dispatch, countriesWasLoaded])

  return (
    <>
      <RadioBlock
        setFieldValue={setFieldValue}
        id="radio_show_option_countries"
        name={SHOW_OPTION}
        value={SHOW_OPTION_COUNTRIES}
        selectedValue={values[SHOW_OPTION]}
        label={t('In a specific country or countries')}
        error={countriesError}
        touched={countiesTouched}
      >
        <MultiSelectBox
          placeholder={t('Add a Country')}
          name={SHOW_COUNTRIES}
          options={formattedCountries}
          value={values[SHOW_COUNTRIES]}
          setFieldValue={setFieldValue}
        />
      </RadioBlock>
      {showRegions && (
        <RegionsRadioBlock
          formik={formik}
          showRegionsCountryFilter={showRegionsCountryFilter}
          formattedCountries={formattedCountries}
          platform={platform}
        />
      )}
      {showCustomLocations && <Locations formik={formik} />}
      {(showWorldwide || showGeolocationsExclusions) && (
        <Spoiler title={t('View more geotargeting options')}>
          <>
            {showWorldwide && (
              <RadioBlock
                setFieldValue={setFieldValue}
                id="radio_show_option_worldwide"
                name={SHOW_OPTION}
                value={SHOW_OPTION_WORLDWIDE}
                selectedValue={values[SHOW_OPTION]}
                label={t('Worldwide')}
              />
            )}
            {showGeolocationsExclusions && (
              <GeolocationsExclusions formik={formik} formattedCountries={formattedCountries} />
            )}
          </>
        </Spoiler>
      )}
    </>
  )
}

Geolocations.propTypes = {
  formik: PropTypes.object,
  showRegionsCountryFilter: PropTypes.bool,
  showRegions: PropTypes.bool,
  showCustomLocations: PropTypes.bool,
  showWorldwide: PropTypes.bool,
  countriesFilter: PropTypes.func,
  showGeolocationsExclusions: PropTypes.bool
}

export default Geolocations
